// AuthContext — Phase 5 Wave 1.
//
// Wires the FE auth state to the new v2 cp-api endpoints
// (`/api/v1/auth/login`, `/auth/logout`, `/auth/session`) via the
// in-memory JWT Bearer access-token store in `lib/api/cpClient`.
//
// The legacy `@intelliauth/api-client` cookie-based flow is replaced
// here. Other api-client call sites (orgs / tenants / members) still
// use the legacy client until their wave migrates them off.
//
// Per LD-JWT in
// `intelliauth-backend/docs/superpowers/specs/2026-05-09-cp-org-console-api-gap-fill.md`
// the access token lives in module-scope memory only — lost on tab
// close / refresh. The Phase 5 wave 1 cut accepts that "refresh →
// /login" as the trade-off; refresh-token exchange is a future
// ticket.

import { createLogger } from '@intelliauth/ui'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import {
  CpV2ApiError,
  getSession,
  logout as apiLogout,
  type OrgMembership,
  setUnauthorizedHandler,
  tryRestoreSession,
  type User,
} from '@/lib/api/cpClient'

const log = createLogger({ component: 'AuthContext' })

// ── Types ───────────────────────────────────────────────────────

/**
 * The v2 SessionResponse uses {id, slug, name, role}. A handful of FE
 * call sites also read .plan and .maxTenants from currentOrg — those
 * are left as Wave-1 placeholders ('' / 0) until the SessionResponse
 * schema (or a follow-up call) supplies them. Tracked as a Phase 6
 * follow-up.
 */
interface OrgWithDetails extends OrgMembership {
  plan: string
  maxTenants: number
}

interface AuthState {
  user: User | null
  organizations: OrgWithDetails[]
  currentOrg: OrgWithDetails | null
  isLoading: boolean
  error: string | null
}

interface AuthContextValue extends AuthState {
  logout: () => Promise<void>
  switchOrg: (orgId: string) => void
  refreshSession: () => Promise<void>
}

// ── Context ─────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Provider ────────────────────────────────────────────────────

const PHASE6_PLAN_PLACEHOLDER = ''
const PHASE6_MAX_TENANTS_PLACEHOLDER = 0

function withPhase6Defaults(m: OrgMembership): OrgWithDetails {
  return {
    ...m,
    plan: PHASE6_PLAN_PLACEHOLDER,
    maxTenants: PHASE6_MAX_TENANTS_PLACEHOLDER,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()

  const [state, setState] = useState<AuthState>({
    user: null,
    organizations: [],
    currentOrg: null,
    isLoading: true,
    error: null,
  })

  const checkSession = useCallback(async (opts?: { silent?: boolean }) => {
    log.debug('Checking session')
    try {
      const session = await getSession()
      const orgs = (session.orgs ?? []).map(withPhase6Defaults)
      log.debug('Session valid', { userId: session.user.id, orgCount: orgs.length })
      setState({
        user: session.user,
        organizations: orgs,
        currentOrg: orgs[0] ?? null,
        isLoading: false,
        error: null,
      })
    } catch (e) {
      const status = e instanceof CpV2ApiError ? e.status : undefined
      const is401 = status === 401
      // Initial mount + post-logout calls pass {silent: true} so a 5xx
      // (e.g. cluster's auth endpoints not yet deployed) doesn't spam a
      // toast on the public /login surface. The toast remains for
      // mid-session re-checks where a sudden 5xx is genuinely a
      // problem the user should know about.
      const silent = opts?.silent === true
      if (is401) {
        log.debug('No active session (401)')
      } else if (!silent) {
        log.error('Session check failed', { status, error: (e as Error).message })
        toast.error('Failed to check session')
      } else {
        log.debug('Session check failed (silent on initial mount)', {
          status,
          error: (e as Error).message,
        })
      }
      setState({
        user: null,
        organizations: [],
        currentOrg: null,
        isLoading: false,
        error: is401 || silent ? null : 'Failed to check session',
      })
    }
  }, [])

  // Bind the global 401 handler. The cpClient's route-aware 401 policy
  // already filters out anonymous endpoints (signup, availability,
  // session probe, login, recover, verify, mfa, invitation preview),
  // so by the time this fires we know the call was a *privileged* one
  // and the session is genuinely expired. Clear local state +
  // redirect.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      log.info('Session expired; clearing local state')
      setState({
        user: null,
        organizations: [],
        currentOrg: null,
        isLoading: false,
        error: null,
      })
      // Don't bounce off public auth flows. Pages like /login,
      // /register, /verify-email, /tenant-admin/accept render their
      // own logged-out UI; redirecting them all to /login causes
      // confusing flicker (e.g. user clicks the register link and
      // gets thrown back to login during the initial /auth/session
      // probe). The login page is the FALLBACK only — if the user is
      // mid-session inside the dashboard, that's where they go.
      const path = typeof window !== 'undefined' ? window.location.pathname : ''
      const publicPrefixes = [
        '/login',
        '/register',
        '/verify-email',
        '/tenant-admin/accept',
        '/invite',
        '/recover',
        '/forbidden',
      ]
      const onPublic = publicPrefixes.some((p) => path.startsWith(p))
      if (!onPublic) {
        navigate('/login')
      }
    })
    return () => setUnauthorizedHandler(() => undefined)
  }, [navigate])

  // Initial mount: try to restore the session from the HttpOnly
  // refresh cookie (set by /auth/login on the previous tab/session)
  // BEFORE asking cp-api "who am I?" — otherwise a hard reload would
  // always answer "nobody" since the in-memory tokens are gone.
  //
  // Sequence:
  //   1. tryRestoreSession() POSTs /auth/refresh with empty body;
  //      browser auto-attaches the ia_cp_refresh cookie. On success,
  //      cpClient sets fresh access + refresh tokens in memory.
  //   2. checkSession() then calls /auth/session WITH the just-
  //      restored access token. 200 → dashboard renders; 401 → user
  //      is genuinely logged out, falls through to login screen.
  //
  // Both steps are silent: on the public /login page, nobody wants
  // a "session restore failed" toast.
  useEffect(() => {
    ;(async () => {
      await tryRestoreSession()
      await checkSession({ silent: true })
    })()
  }, [checkSession])

  const logout = useCallback(async () => {
    log.info('Logging out')
    await apiLogout() // adapter swallows network errors + clears token
    log.debug('Logout complete, clearing state')
    setState({
      user: null,
      organizations: [],
      currentOrg: null,
      isLoading: false,
      error: null,
    })
    toast.success('Signed out successfully')
    navigate('/login')
  }, [navigate])

  const switchOrg = useCallback((orgId: string) => {
    setState((prev) => {
      const org = prev.organizations.find((o) => o.id === orgId)
      return org ? { ...prev, currentOrg: org } : prev
    })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      logout,
      switchOrg,
      refreshSession: checkSession,
    }),
    [state, logout, switchOrg, checkSession],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hook ────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
